// Pure helpers for the sign-up draft the RegistrationStepper keeps in
// sessionStorage so a refresh mid-registration doesn't lose the owner's
// progress. No Vue or Firebase in here - the stepper owns the watchers and
// the storage calls; this module only shapes and migrates the payloads.

export const REGISTRATION_DRAFT_KEY = "lulu_business_registration_draft_v1";
export const LOGO_DRAFT_KEY = "lulu_business_registration_logo_v1";

// Draft payload versions:
//   1 - a Welcome screen sat at step 0, followed by five steps.
//   2 - Welcome removed: basics, branding, web_page, qr_design, plan.
//   3 - the three optional steps merged into one: basics, customise, plan.
export const DRAFT_VERSION = 3;
const LOGO_DRAFT_VERSION = 1;

// Where each version-2 step index lands in the three-step flow.
const V2_TO_V3_STEP = { 0: 0, 1: 1, 2: 1, 3: 1, 4: 2 };

// Fields safe to persist across refresh. Excludes derived values
// (businessId, id, adminEmail, logoImageName), logo bytes (not
// serialisable), and the terms checkbox (deliberate action, re-affirm).
export const PERSISTED_DRAFT_FIELDS = [
  "adminName",
  "displayName",
  "numberOfLocations",
  "thresholdForRedeeming",
  "auxiliaryFieldLabel",
  "auxiliaryFieldValue",
  "backgroundColor",
  "foregroundColor",
  "title",
  "titleCustomised",
  "subtitle",
  "subtitleCustomised",
  "qrHeadline",
  "qrHeadlineCustomised",
  "qrShowLogo",
  "billingPeriod",
  // The plan the owner picked on the last step ("" until they have).
  "packagePlan",
  // Whether the Established enquiry has already been sent, so a refresh
  // doesn't invite a duplicate.
  "establishedEnquirySent",
];

export const serialiseDraft = ({ email, currentStep, formData }) =>
  JSON.stringify({
    version: DRAFT_VERSION,
    email,
    currentStep,
    formData: PERSISTED_DRAFT_FIELDS.reduce((acc, key) => {
      acc[key] = formData[key];
      return acc;
    }, {}),
  });

// Older drafts counted their steps differently, so the stored index is
// walked forward one version at a time until it matches the current flow.
const migrateStep = (version, step) => {
  let current = Number.isInteger(step) ? step : 0;
  let v = version;
  if (v === 1) {
    current -= 1; // drop the Welcome screen
    v = 2;
  }
  if (v === 2) {
    current = V2_TO_V3_STEP[current] ?? 0;
    v = 3;
  }
  return v === DRAFT_VERSION ? current : null;
};

// Returns { formData, currentStep } or null when the payload is unusable or
// belongs to a different signed-in email. `lastStep` is the highest step
// index the stepper can show, used to clamp the restored step.
export const parseDraft = (raw, { email, lastStep }) => {
  if (!raw || !email) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_) {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  if (parsed.email !== email) return null;

  const step = migrateStep(parsed.version, parsed.currentStep);
  if (step === null) return null;

  const source =
    parsed.formData && typeof parsed.formData === "object" ? parsed.formData : {};
  const formData = {};
  for (const key of PERSISTED_DRAFT_FIELDS) {
    if (key in source) formData[key] = source[key];
  }

  return {
    formData,
    currentStep: Math.min(Math.max(step, 0), lastStep),
  };
};

// Serialises the chosen logo as a data URL so it survives a refresh. Files
// are capped at 1.5 MB upstream, so the payload stays within storage quota.
export const encodeLogoDraft = (file, email) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(
        JSON.stringify({
          version: LOGO_DRAFT_VERSION,
          email,
          fileName: file.name,
          type: file.type,
          dataUrl: reader.result,
        }),
      );
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// Rebuilds the File from a payload written by encodeLogoDraft. Returns null
// for anything unusable or saved under another email.
export const decodeLogoDraft = (raw, email) => {
  if (!raw || !email) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== LOGO_DRAFT_VERSION) return null;
    if (parsed.email !== email) return null;
    const dataUrl = String(parsed.dataUrl || "");
    const commaIdx = dataUrl.indexOf(",");
    if (commaIdx === -1) return null;
    const binary = atob(dataUrl.slice(commaIdx + 1));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const type = parsed.type || "image/png";
    return new File([bytes], parsed.fileName || "logo.png", { type });
  } catch (_) {
    return null;
  }
};
